import {
  ReactNode,
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Tooltip,
  TooltipContentProps,
  TooltipProps,
  useChartHeight,
  useChartWidth,
  useYAxisInverseScale,
} from 'recharts';

import { registerTooltipDismissal } from './utils/tooltipDismissal';
import { trackPointerHover } from './utils/trackPointerHover';

export interface CursorPoint {
  /** Pointer position relative to the chart wrapper, in pixels. */
  chartX: number;
  chartY: number;

  /** Pointer position mapped through the y axis, if it resolves. */
  yValue: number | null;
}

const CursorPositionContext = createContext<CursorPoint | null>(null);

/** The pointer's position for tooltip content rendered by CursorTooltip. */
export const useCursorPosition = () => useContext(CursorPositionContext);

interface CursorState {
  chartX: number;
  chartY: number;
  rectLeft: number;
  rectTop: number;
}

interface Size {
  width: number;
  height: number;
}

interface ContentEnvironment {
  content: NonNullable<TooltipProps<number, string>['content']>;
  cursorPoint: CursorPoint | null;
  measured: boolean;
  measureRef: (node: HTMLDivElement | null) => void;
  measure: () => void;
  maxWidth: number;
}

const ContentEnvironmentContext = createContext<ContentEnvironment | null>(
  null
);

/**
 * Recharts mounts its content prop as a component type, so the type must be
 * referentially stable or the content remounts on every pointer move, which
 * restarts enter transitions and keeps the tooltip invisible while the
 * pointer is moving. Everything that varies flows in through context.
 */
const StableTooltipContent = (props: TooltipContentProps<number, string>) => {
  const environment = useContext(ContentEnvironmentContext);
  const payloadRef = useRef(props.payload);
  if (
    payloadRef.current.length !== props.payload.length ||
    payloadRef.current.some((entry, index) => entry !== props.payload[index])
  ) {
    payloadRef.current = props.payload;
  }
  const payload = payloadRef.current;
  const measure = environment?.measure;
  useLayoutEffect(() => {
    measure?.();
  }, [
    measure,
    props.active,
    props.label,
    payload,
    environment?.content,
    environment?.maxWidth,
  ]);
  if (!environment) {
    return null;
  }
  const { content, cursorPoint, measured, measureRef, maxWidth } = environment;

  const inner: ReactNode =
    typeof content === 'function'
      ? content(props)
      : isValidElement(content)
        ? cloneElement(content, props)
        : null;

  return (
    <CursorPositionContext.Provider value={cursorPoint}>
      <div
        ref={measureRef}
        style={{
          display: 'inline-block',
          maxWidth,
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
          visibility: measured ? 'visible' : 'hidden',
        }}
      >
        {inner}
      </div>
    </CursorPositionContext.Provider>
  );
};

export interface CursorTooltipProps extends Omit<
  TooltipProps<number, string>,
  'content'
> {
  content: NonNullable<TooltipProps<number, string>['content']>;

  /** Gap in pixels between the pointer and the tooltip box. */
  cursorOffset?: number;

  /** Recharts synchronization group whose tooltips share hover dismissal. */
  syncId?: string | number;
}

/**
 * A recharts Tooltip that trails the pointer instead of snapping to the
 * active tick, so the hovered bars and spans stay visible. The box sits a
 * small offset from the cursor, flipping to the other side near the chart
 * edges and clamping to both the chart and the window viewport. Content
 * can read the pointer through useCursorPosition().
 */
const CursorTooltip = ({
  content,
  cursorOffset = 14,
  syncId,
  ...rest
}: CursorTooltipProps) => {
  const anchorRef = useRef<SVGGElement>(null);
  const chartWidth = useChartWidth();
  const chartHeight = useChartHeight();
  const yInverseScale = useYAxisInverseScale();
  const [cursor, setCursor] = useState<CursorState | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [size, setSize] = useState<Size | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const svg = anchorRef.current?.ownerSVGElement;
    if (!svg) {
      return;
    }

    let ownsHover = false;
    const dismissal = registerTooltipDismissal((value) => {
      setDismissed(value);
      if (value || !ownsHover) setCursor(null);
    }, syncId);

    const handleMove = (event: PointerEvent) => {
      const rect = svg.getBoundingClientRect();
      const next = {
        chartX: Math.round(event.clientX - rect.left),
        chartY: Math.round(event.clientY - rect.top),
        rectLeft: Math.round(rect.left),
        rectTop: Math.round(rect.top),
      };
      ownsHover = true;
      dismissal.update(false);
      setCursor((prev) =>
        prev &&
        prev.chartX === next.chartX &&
        prev.chartY === next.chartY &&
        prev.rectLeft === next.rectLeft &&
        prev.rectTop === next.rectTop
          ? prev
          : next
      );
    };

    const handleLeave = () => {
      ownsHover = false;
      setCursor(null);
      dismissal.update(true);
    };
    const handleKeyboard = () => {
      ownsHover = true;
      setCursor(null);
      dismissal.update(false);
    };
    const stopTracking = trackPointerHover(svg, {
      onMove: handleMove,
      onLeave: handleLeave,
      deferMoves: false,
    });
    svg.addEventListener('focusin', handleKeyboard);
    svg.addEventListener('keydown', handleKeyboard);
    return () => {
      if (ownsHover) dismissal.update(true);
      dismissal.dispose();
      stopTracking();
      svg.removeEventListener('focusin', handleKeyboard);
      svg.removeEventListener('keydown', handleKeyboard);
    };
  }, [syncId]);

  const measure = useCallback(() => {
    const node = contentRef.current;
    if (!node) return;
    const next = { width: node.offsetWidth, height: node.offsetHeight };
    setSize((prev) =>
      prev && prev.width === next.width && prev.height === next.height
        ? prev
        : next
    );
  }, []);

  const measureRef = useCallback(
    (node: HTMLDivElement | null) => {
      contentRef.current = node;
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!node) {
        return;
      }
      const observer = new ResizeObserver(measure);
      observer.observe(node);
      observerRef.current = observer;
      measure();
    },
    [measure]
  );

  const position = useMemo(() => {
    if (!cursor || !size) {
      return undefined;
    }

    // Trail the cursor, flip when the box would leave the chart, then
    // clamp to whichever of the chart and window viewport is tighter:
    const place = (
      coord: number,
      extent: number,
      chartExtent: number,
      rectOffset: number,
      viewportExtent: number
    ) => {
      let p = coord + cursorOffset;
      if (p + extent > chartExtent) {
        p = coord - cursorOffset - extent;
      }
      p = Math.min(
        p,
        chartExtent - extent,
        viewportExtent - rectOffset - extent
      );
      return Math.max(p, 0, -rectOffset);
    };

    return {
      x: place(
        cursor.chartX,
        size.width,
        chartWidth,
        cursor.rectLeft,
        window.innerWidth
      ),
      y: place(
        cursor.chartY,
        size.height,
        chartHeight,
        cursor.rectTop,
        window.innerHeight
      ),
    };
  }, [cursor, size, chartWidth, chartHeight, cursorOffset]);

  const cursorPoint = useMemo<CursorPoint | null>(() => {
    if (!cursor) {
      return null;
    }
    const yValue = yInverseScale?.(cursor.chartY);
    return {
      chartX: cursor.chartX,
      chartY: cursor.chartY,
      yValue:
        typeof yValue === 'number' && Number.isFinite(yValue) ? yValue : null,
    };
  }, [cursor, yInverseScale]);

  const environment = useMemo<ContentEnvironment>(
    () => ({
      content,
      cursorPoint,
      measured: size != null,
      measureRef,
      measure,
      maxWidth: Math.max(1, chartWidth - 24),
    }),
    [content, cursorPoint, size, measureRef, measure, chartWidth]
  );

  return (
    <ContentEnvironmentContext.Provider value={environment}>
      <g ref={anchorRef} />
      <Tooltip
        {...rest}
        active={dismissed ? false : rest.active}
        wrapperStyle={{
          ...rest.wrapperStyle,
          pointerEvents: 'none',
          zIndex: Math.max(10, Number(rest.wrapperStyle?.zIndex) || 0),
          maxWidth: 'calc(100vw - 24px)',
        }}
        isAnimationActive={false}
        position={position}
        content={StableTooltipContent}
      />
    </ContentEnvironmentContext.Provider>
  );
};

export default CursorTooltip;
